import { getExampleNumber, isValidPhoneNumber } from 'libphonenumber-js/max';
import type { CountryCode } from 'libphonenumber-js/max';
import examples from 'libphonenumber-js/examples.mobile.json';
import { z } from 'zod/v4';

import type { PhoneNumber } from '@klareospace/pharos-lib-types';

import { stripTrunkPrefix } from '../utils/phone-helpers';

/**
 * Strict validity check for a digits-only national number (as stored in `PhoneNumber.number`)
 * against a specific, already-known country's full numbering plan — length AND allowed leading
 * digits. For callers that don't already know the country (e.g. validating a `{ dialCode, number }`
 * pair where the dial code may be shared by several countries), use `isBlankOrValidPhone` instead,
 * which resolves the region from the full E.164 string rather than assuming one.
 *
 * Uses the "max" libphonenumber-js metadata, unlike the legacy `PHONE_NATIONAL_NUMBER_REGEX` which
 * only checks digit count.
 */
export function isValidNationalNumber(number: string, country: CountryCode): boolean {
  if (!number) return false;
  try {
    return isValidPhoneNumber(number, country);
  } catch {
    return false;
  }
}

/** Builds the E.164 string libphonenumber-js needs to resolve the actual region itself. */
function toE164(phone: PhoneNumber): string {
  const digitsOnlyDialCode = phone.dialCode.replace(/\D/g, '');
  return `+${digitsOnlyDialCode}${phone.number.trim()}`;
}

/**
 * True when `phone` is absent, or its `number` is blank, or its `number` is valid. The single
 * predicate behind every "optional phone" validation rule in the apps — a blank number is always
 * acceptable; a non-blank one must be valid.
 *
 * Validates the full E.164 string (dial code + number) and lets libphonenumber-js resolve the
 * actual region itself, rather than guessing a single "main" country for the dial code — a dial
 * code like `+1` or `+7` is shared by dozens of countries (US/CA/..., RU/KZ/...), each with its
 * own numbering plan, so pinning validation to one of them would reject valid numbers from every
 * other country sharing that code.
 */
export function isBlankOrValidPhone(phone: PhoneNumber | null | undefined): boolean {
  if (!phone) return true;
  if (!phone.number.trim()) return true;
  return isValidPhoneNumber(toE164(phone));
}

/**
 * Returns a formatted, country-appropriate example national number (e.g. FR → `'6 12 34 56 78'`),
 * for use as a dynamic input placeholder. Returns `''` defensively when libphonenumber-js has no
 * example for the given country.
 */
export function getExampleNationalNumber(country: CountryCode): string {
  const example = getExampleNumber(country, examples);
  if (!example) return '';
  return stripTrunkPrefix(example.formatNational(), example.nationalNumber);
}

// ─── Zod schema factories ───────────────────────────────────────────────────────

export interface PhoneNumberSchemaMessages {
  invalid?: string;
  required?: string;
}

/** Message options accepted by the optional/nullable phone schema factories — no `required` slot. */
export interface OptionalPhoneNumberSchemaMessages {
  invalid?: string;
}

/** Shared shape behind every phone schema factory below — only optionality/nullability differs. */
type PhoneNumberShape = z.ZodObject<{ dialCode: z.ZodString; number: z.ZodString }>;

export type PhoneNumberSchema = PhoneNumberShape;
export type OptionalPhoneNumberSchema = z.ZodOptional<z.ZodNullable<PhoneNumberShape>>;
export type NullablePhoneNumberSchema = z.ZodNullable<PhoneNumberShape>;

/**
 * A required `{ dialCode, number }` phone schema: `number` must be non-empty and valid
 * (see `isBlankOrValidPhone`).
 */
export function buildPhoneNumberSchema(messages: PhoneNumberSchemaMessages = {}): PhoneNumberSchema {
  return z
    .object({
      dialCode: z.string().min(1),
      number: z.string().trim().min(1, messages.required),
    })
    .refine((phone) => isBlankOrValidPhone(phone), messages.invalid);
}

/**
 * An optional/nullable variant of `buildPhoneNumberSchema` for forms where the phone number is
 * not mandatory — an empty `number` passes, but a non-empty one must still be valid.
 */
export function buildOptionalPhoneNumberSchema(
  messages: OptionalPhoneNumberSchemaMessages = {},
): OptionalPhoneNumberSchema {
  return z
    .object({
      dialCode: z.string(),
      number: z.string(),
    })
    .refine((phone) => isBlankOrValidPhone(phone), messages.invalid)
    .nullable()
    .optional();
}

/**
 * A nullable (but NOT optional) variant of `buildPhoneNumberSchema`, for forms whose field is
 * always present in the shape (`PhoneNumber | null`, never `undefined`) but not mandatory to fill.
 */
export function buildNullablePhoneNumberSchema(
  messages: OptionalPhoneNumberSchemaMessages = {},
): NullablePhoneNumberSchema {
  return z
    .object({
      dialCode: z.string(),
      number: z.string(),
    })
    .refine((phone) => isBlankOrValidPhone(phone), messages.invalid)
    .nullable();
}
